/**
 * Visual validation & image asset verification utilities.
 * 
 * Ensures that PDF documents, raw filenames, broken references, or non-image assets
 * are NEVER passed into <img> elements or visual showcase renderers as images.
 * Only valid, renderable image URLs (e.g. svg, jpg, png, webp) or page-extracted images
 * are allowed to render.
 */

/**
 * Validates whether a given string is a valid renderable web image URL.
 * 
 * Rejects:
 * - null, undefined, empty string, whitespace
 * - strings like "null", "undefined", "[object Object]", "empty"
 * - raw PDF files (e.g. "...something.pdf" or "...something.pdf?...")
 * - non-image formats (.webm, .mp4, .ogv, .tif, .tiff, .doc, .docx, .txt)
 * - plain filenames without URL scheme (e.g. "Lectures on the diseases.pdf")
 * 
 * Accepts:
 * - http(s) URLs pointing to valid image extensions (svg, png, jpg, jpeg, webp, gif, avif)
 * - Wikimedia page-rendered thumbnails of PDFs (e.g. ".../page1-500px-...pdf.jpg")
 * - data:image/... base64 URIs
 * - relative image paths (e.g. "/diagram.png")
 */
export function isValidRenderableImageUrl(url: unknown): url is string {
  if (!url || typeof url !== 'string') return false;

  const trimmed = url.trim();
  if (!trimmed) return false;

  const lower = trimmed.toLowerCase();
  if (
    lower === 'null' ||
    lower === 'undefined' ||
    lower === '[object object]' ||
    lower === 'empty' ||
    lower === 'none' ||
    lower === 'false' ||
    lower === 'true'
  ) {
    return false;
  }

  // Must be an HTTP(S) URL, relative image path, or data URI
  const isHttp = lower.startsWith('http://') || lower.startsWith('https://');
  const isDataImg = lower.startsWith('data:image/');
  const isRelative = lower.startsWith('/') && !lower.startsWith('//');

  if (!isHttp && !isDataImg && !isRelative) {
    return false;
  }

  // Inspect the clean path before query parameters or hash fragments
  const cleanPath = lower.split('?')[0].split('#')[0];

  // Raw document/video/non-image extensions that CANNOT be rendered in an <img> tag
  const nonImageExtensions = [
    '.pdf',
    '.webm',
    '.mp4',
    '.ogv',
    '.ogg',
    '.mov',
    '.tif',
    '.tiff',
    '.doc',
    '.docx',
    '.txt',
    '.zip',
    '.tar',
    '.gz',
    '.rar'
  ];

  for (const ext of nonImageExtensions) {
    if (cleanPath.endsWith(ext)) {
      return false;
    }
  }

  // If it's a data image, it's valid
  if (isDataImg) return true;

  // Check if it has a known image extension or is an image service endpoint
  const knownImageExtensions = ['.svg', '.png', '.jpg', '.jpeg', '.webp', '.gif', '.avif', '.bmp'];
  const hasImageExt = knownImageExtensions.some(ext => cleanPath.endsWith(ext));

  // If it has a known image extension, it's definitely valid (like Wikimedia ...page1-500px-...pdf.jpg)
  if (hasImageExt) return true;

  // If it's a URL without an explicit extension, make sure it does NOT contain raw .pdf references
  // e.g. "https://upload.wikimedia.org/.../something.pdf?utm_source=..."
  if (lower.includes('.pdf') && !lower.includes('.pdf.jpg') && !lower.includes('.pdf.png') && !lower.includes('.pdf.webp')) {
    return false;
  }

  return true;
}

/**
 * Safely extracts a valid renderable image URL from a step if present.
 */
export function getValidStepImageUrl(step: any): string | undefined {
  if (!step) return undefined;
  if (isValidRenderableImageUrl(step.image_url)) return step.image_url;
  if (isValidRenderableImageUrl(step.static_image_url)) return step.static_image_url;
  return undefined;
}

/**
 * Safely extracts a valid renderable image URL from top-level lesson/solution data if present.
 */
export function getValidLessonImageUrl(data: any): string | undefined {
  if (!data) return undefined;
  if (isValidRenderableImageUrl(data.static_image_url)) return data.static_image_url;
  return undefined;
}
